/**
 * One file row into one SATU record (section 17): the normalisers of ./normalise applied
 * per target column, the parish's territory and family lists consulted, the shared Zod
 * validators run over the result, and every finding recorded against the row as an
 * issue. Then the writes a ready row becomes at commit time.
 *
 * Issues have three kinds. `galat` skips the row (an unreadable birth or death date, an
 * unknown lingkungan, a missing KK number, a validator refusal). `duplikat` holds the row
 * until the desk ticks "Tetap simpan" (a person of the same name and birth already on the
 * device; release 18: a government KK number or a Kode keluarga another family holds, or an
 * earlier row of the file; review I-2: a family with neither, whose head and lingkungan an
 * earlier row or a family on the device has). `catatan` is reported but does not block: an
 * unknown occupation label, or an unreadable optional value (a landline, a NIK Excel shortened,
 * a kode pos), dropped so the family or person is still imported (uji D2).
 *
 * Release 18 (owner, 2026-10-08): a file's "Nomor KK" is the government number when it is 16
 * digits, else the family's Kode keluarga; a family's parish number is SATU's, given in the
 * file's order as it is saved (`beriNomorParoki`), never read from a file. A person row joins
 * its family by whichever value the file has: the government number, the code, or the parish
 * number an earlier import gave.
 *
 * Release 19 (owner, 2026-10-08): a person row reads the parish data sheet's fields too: the
 * religion (a religion other than Katolik makes a person "Bukan Katolik" when no Status gereja
 * column says otherwise), the sacraments received elsewhere (`referensi_sakramen_luar`: a
 * certificate naming only its church has that church as its parish), the family's housing
 * (written to the family) and, for parish staff and admins only (`denganProfil`), the sensitive
 * fields, written to `umat_profil` under the person's own id. A value that does not read is
 * emptied with a note and the row is kept.
 *
 * Pure and platform-neutral: the worker's answers (units, families, duplicate hits) come
 * in as arguments so the tests run under Node and the screen can process rows in chunks.
 */

import { type AgamaUmat, selaraskanAgama } from '../dataUmat'
import {
  gabungNamaKepala,
  kunciKodeKeluarga,
  nomorKkDalamKurung,
  nomorKkSebaris,
  nomorKkTampil,
  peringatanKode,
  peringatanNomorKk,
  rapikanKodeKeluarga,
} from '../nomorKk'
import { bacaKeterampilan } from '../pendaftaran'
import type { STATUS_GEREJA } from '../schema/contract'
import { format } from '../strings/index'
import ui from '../strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit } from '../strings/unit'
import {
  angkaNomorKk,
  GANDA_KODE_PANJANG,
  nomorKkSesudah,
  type PemakaiKk,
  type PemakaiKode,
} from '../sync/ganda'
import type { LocalWrite } from '../sync/ops'
import { uuidv7 } from '../sync/ops'
import type { Keluarga, ReferensiSakramenLuar, Umat } from '../validators/index'
import {
  emailSchema,
  type IssueRingkas,
  keluargaSchema,
  masalahPerKolom,
  petaGalatZod,
  umatSchema,
} from '../validators/index'
import {
  bacaAgama,
  bacaAnakKe,
  bacaAnggotaCu,
  bacaAsuransi,
  bacaGolonganDarah,
  bacaJenisBaptis,
  bacaJenisPerkawinan,
  bacaPendapatan,
  bacaPendidikanSaatIni,
  bacaStatusRumah,
  bacaTahun,
  denominasiDari,
  levelDariAwalan,
  rapikanSuku,
  sekolahDariPendidikan,
} from './dataUmat'
import { type JenisImpor, type KolomTarget, type KunciSakramenImpor, kolomTarget } from './mapping'
import {
  anakKeDari,
  bacaDigit,
  bacaHubungan,
  bacaJenisKelamin,
  bacaKolomNomorKk,
  bacaNomorKk,
  bacaPekerjaan,
  bacaPendidikan,
  bacaStatusGereja,
  bacaStatusHidup,
  bacaStatusPerkawinan,
  bacaTanggal,
  bacaTelepon,
  cariUnit,
  type Hasil,
  kunciTeks,
  type NomorKkBerkas,
  pisahNama,
  rapikanNama,
  rapikanTeks,
  type UnitRujukan,
} from './normalise'

const pesan = ui.impor.pesan
const teksPratinjau = ui.impor.pratinjauLanjut

type StatusGereja = (typeof STATUS_GEREJA)[number]

/** The sacraments a person file may give from elsewhere, in the template's order. */
const SAKRAMEN: readonly KunciSakramenImpor[] = ['baptis', 'komuni', 'krisma', 'perkawinan']

/** A file's family before SATU gives it its parish number (release 18): checked without it. */
const keluargaImporSchema = keluargaSchema.omit({ nomor_kk_katolik: true })

export type JenisMasalah = 'galat' | 'duplikat' | 'catatan'

/** The plan's RowIssue, with the kind that decides whether the row is saved. */
export interface RowIssue {
  row: number
  field?: string | undefined
  message: string
  /** A umat id on the device, or `baris:<n>` for a row earlier in the same file. */
  duplicateOf?: string | undefined
  kind: JenisMasalah
}

export interface KeluargaRujukan {
  id: string
  /** The parish number (`nomor_kk_katolik`). */
  nomorKk: string
  /** Release 18: the government KK number, when the family has one. */
  nomorKkPemerintah?: string | null | undefined
  /** Release 18: the family's Kode keluarga, when it has one. */
  kodeKeluarga?: string | null | undefined
  lingkunganId: string | null
  kepalaId: string | null
  kepala: string | null
  /** aktif, pindah or bubar, as the device reads it (`impor:rujukan`); absent reads as aktif. */
  status?: string | undefined
}

/** The number a family is named by in a sentence: its shown number on one line. */
export function nomorKkRujukan(k: KeluargaRujukan): string {
  return nomorKkSebaris(nomorKkTampil(k.nomorKkPemerintah, k.nomorKk))
}

export interface Rujukan {
  lingkungan: readonly UnitRujukan[]
  keluarga: readonly KeluargaRujukan[]
}

/** A person on the device who may be the same as a row (the worker's answer). */
export interface DuplikatUmat {
  umatId: string
  nama: string
  tanggalLahir: string | null
  nomorKk: string | null
  keluargaId: string | null
  /**
   * Release 19: the person's stored outside references, so a completing row's sacraments are
   * merged onto them. Absent (not asked), a completing row writes none: never over references
   * it cannot see.
   */
  referensiLuar?: ReferensiSakramenLuar | null | undefined
  /** Release 19: the sacraments linked to an entry of this parish's book; the file's are dropped. */
  tertaut?: readonly KunciSakramenImpor[] | undefined
  /**
   * Review I-2: the person's church status and religion as stored, which a completing row's are
   * reconciled with (`agamaPerbarui`). Absent (not asked), a stored Catholic is assumed.
   */
  statusGereja?: string | null | undefined
  agama?: string | null | undefined
}

export interface BarisImpor {
  /** The sheet row number as the clerk sees it (the header is row 1). */
  baris: number
  jenis: JenisImpor
  /** Normalised columns of the target table (keluarga or umat); null where the file was empty. */
  fields: Record<string, unknown>
  /** Keluarga rows: the head's name from the file, for the head's umat row. */
  kepalaNama: string | null
  /** Umat rows: the family the row joins, from the KK number. */
  keluarga: KeluargaRujukan | null
  /**
   * The "Nomor KK" cell as read (release 18): a government number, a code, or a parish number;
   * the preview shows it. Null when the cell was empty.
   */
  nomorKkBerkas: string | null
  /** The lingkungan name as read, for the preview cell. */
  lingkunganNama: string | null
  masalah: RowIssue[]
  /** Umat rows: the same person already on the device, to complete instead of duplicate. */
  perbarui: DuplikatUmat | null
  /**
   * Umat rows (release 19): the sensitive fields read for parish staff and admins
   * (`MasukanBaris.denganProfil`), written to `umat_profil`; null when not read.
   */
  profil?: Record<string, unknown> | null | undefined
  /** Umat rows (release 19): the family's housing the row gives, written to its family. */
  statusRumah?: string | null | undefined
  /**
   * Umat rows (release 19): the church status the file states, from its Status gereja column or
   * an older file's "Agama" holding one ("Katekumen"); null when it states none.
   */
  statusBerkas?: string | null | undefined
  /** Umat rows (release 19): the religion the file gives, as read; null when it gives none. */
  agamaBerkas?: string | null | undefined
}

export type KeadaanBaris = 'siap' | 'bermasalah' | 'duplikat' | 'melengkapi'

/** The worded state of a row; `tetapSimpan` lifts a duplicate hold. */
export function keadaanBaris(b: BarisImpor, tetapSimpan: boolean): KeadaanBaris {
  if (b.masalah.some((m) => m.kind === 'galat')) return 'bermasalah'
  if (b.masalah.some((m) => m.kind === 'duplikat') && !tetapSimpan) return 'duplikat'
  if (b.perbarui !== null) return 'melengkapi'
  return 'siap'
}

export function akanDisimpan(b: BarisImpor, tetapSimpan: boolean): boolean {
  const k = keadaanBaris(b, tetapSimpan)
  return k === 'siap' || k === 'melengkapi'
}

export interface RingkasanBaris {
  siap: number
  /** Held for "Tetap simpan" (release 18: never counted as Bermasalah). */
  ditahan: number
  bermasalah: number
}

export function ringkasBaris(
  rows: readonly BarisImpor[],
  tetapSimpan: ReadonlySet<number>,
): RingkasanBaris {
  let siap = 0
  let ditahan = 0
  for (const b of rows) {
    const k = keadaanBaris(b, tetapSimpan.has(b.baris))
    if (k === 'duplikat') ditahan += 1
    else if (k !== 'bermasalah') siap += 1
  }
  return { siap, ditahan, bermasalah: rows.length - siap - ditahan }
}

// ---------------------------------------------------------------------------
// Reading one row
// ---------------------------------------------------------------------------

interface Konteks {
  baris: number
  jenis: JenisImpor
  masalah: RowIssue[]
  /** The parish's word for its smallest unit: the sentences name the column in it. */
  sebutan: SebutanUnit
}

function galat(ctx: Konteks, field: string | undefined, message: string): void {
  ctx.masalah.push({ row: ctx.baris, field, message, kind: 'galat' })
}

function catatan(ctx: Konteks, field: string, message: string): void {
  ctx.masalah.push({ row: ctx.baris, field, message, kind: 'catatan' })
}

function labelKolom(ctx: Konteks, id: string): string {
  return kolomTarget(ctx.jenis, ctx.sebutan).find((k) => k.id === id)?.label ?? id
}

/** A required reading failed to read: an error naming the column and the cell. */
function tidakTerbaca(ctx: Konteks, id: string, nilai: string): void {
  galat(ctx, id, format(pesan.tidakTerbaca, { kolom: labelKolom(ctx, id), nilai }))
}

/** A code that did not match: the value is dropped and the row carries a note. */
function kodeTidakDikenal(ctx: Konteks, id: string, nilai: string): null {
  catatan(ctx, id, format(pesan.kodeTidakDikenal, { kolom: labelKolom(ctx, id), nilai }))
  return null
}

/** An optional value that did not read (uji D2): dropped, the cell named in a note. */
function dikosongkan(ctx: Konteks, id: string, nilai: string): null {
  catatan(ctx, id, format(pesan.tidakTerbacaDikosongkan, { kolom: labelKolom(ctx, id), nilai }))
  return null
}

/**
 * A reading's value. When it fails: `galat` blocks the row (a required or life-event value),
 * `catatan` drops an unknown code, `kosongkan` drops an unreadable optional value; both notes
 * name the cell.
 */
function ambil<T>(
  ctx: Konteks,
  id: string,
  raw: string | undefined,
  hasil: Hasil<T>,
  onGagal: 'galat' | 'catatan' | 'kosongkan',
): T | null {
  if (hasil.jenis === 'nilai') return hasil.nilai
  if (hasil.jenis === 'kosong') return null
  const nilai = rapikanTeks(raw) ?? ''
  if (onGagal === 'galat') tidakTerbaca(ctx, id, nilai)
  else if (onGagal === 'catatan') kodeTidakDikenal(ctx, id, nilai)
  else dikosongkan(ctx, id, nilai)
  return null
}

/**
 * The validators' refusals, one per column the row has no error on yet, in the app's words
 * (../validators/galat: the schemas are parsed with `petaGalatZod`, never Zod's English).
 */
function zodIssues(
  ctx: Konteks,
  issues: readonly IssueRingkas[],
  alias: Readonly<Record<string, string>> = {},
): void {
  for (const m of masalahPerKolom(issues)) {
    const id = alias[m.kolom] ?? m.kolom
    const already = ctx.masalah.some((x) => x.field === id && x.kind === 'galat')
    if (already) continue
    galat(ctx, id, m.pesan)
  }
}

/**
 * A document number (akta lahir) as text: never read as a number, so leading zeros stay;
 * Excel's text mark (a leading apostrophe) goes, as for the KK numbers.
 */
function bacaNomorTeks(text: string | undefined): string | null {
  return rapikanTeks(text?.replace(/^\s*'+/, ''))
}

export interface MasukanBaris {
  baris: number
  /** Raw cell per target id (from `nilaiBaris`). */
  nilai: Readonly<Record<string, string>>
  rujukan: Rujukan
  /** The parish's word for its smallest unit (Sebutan unit terkecil); the default when absent. */
  sebutan?: SebutanUnit | undefined
  /**
   * Release 18: "Belum punya KK Disdukcapil" ticked at mapping: a family's empty Nomor KK is
   * what the desk said, not worth a note on every row.
   */
  tanpaKk?: boolean | undefined
  /**
   * Release 19: the reader is parish staff or an admin (`bolehDataSensitif`): the sensitive
   * columns are read. Otherwise they are not read at all: no value, no note.
   */
  denganProfil?: boolean | undefined
}

/**
 * The families of a list by their parish number, by its digits without leading zeros, by their
 * government number and by their code (`kunciKodeKeluarga`).
 */
interface IndeksKk {
  persis: Map<string, KeluargaRujukan>
  angka: Map<string, KeluargaRujukan[]>
  lebar: number | null
  pemerintah: Map<string, KeluargaRujukan[]>
  kode: Map<string, KeluargaRujukan[]>
  /** By lingkungan and head (`kunciKepala`), for a family row with neither value (review I-2). */
  kepala: Map<string, KeluargaRujukan[]>
}

/** A family as a file without numbers names it: its lingkungan and its head's name key. */
function kunciKepala(lingkunganId: string, kepala: string): string {
  return `${lingkunganId}|${kunciOrang(kepala)}`
}

function tambah(peta: Map<string, KeluargaRujukan[]>, kunci: string, k: KeluargaRujukan): void {
  const ada = peta.get(kunci)
  if (ada === undefined) peta.set(kunci, [k])
  else ada.push(k)
}

/**
 * Built once per list (final review 3, Minor 4): a 5,000-row file against 5,000 families asked
 * every row for every family before, two regular expressions a pair.
 */
const indeksKk = new WeakMap<readonly KeluargaRujukan[], IndeksKk>()

function indeks(daftar: readonly KeluargaRujukan[]): IndeksKk {
  const ada = indeksKk.get(daftar)
  if (ada !== undefined) return ada
  const persis = new Map<string, KeluargaRujukan>()
  const angka = new Map<string, KeluargaRujukan[]>()
  const pemerintah = new Map<string, KeluargaRujukan[]>()
  const kode = new Map<string, KeluargaRujukan[]>()
  const kepala = new Map<string, KeluargaRujukan[]>()
  const digit: string[] = []
  for (const k of daftar) {
    const p = k.nomorKkPemerintah?.trim() ?? ''
    if (p !== '') tambah(pemerintah, p, k)
    const kunci = kunciKodeKeluarga(k.kodeKeluarga)
    if (kunci !== null) tambah(kode, kunci, k)
    if (k.lingkunganId !== null && k.kepala !== null && k.kepala.trim() !== '') {
      tambah(kepala, kunciKepala(k.lingkunganId, k.kepala), k)
    }
    if (!persis.has(k.nomorKk)) persis.set(k.nomorKk, k)
    const a = angkaNomorKk(k.nomorKk)
    if (a === null) continue
    digit.push(k.nomorKk)
    tambah(angka, a, k)
  }
  const [pertama] = digit
  const lebar =
    pertama === undefined ||
    !digit.some((n) => n.startsWith('0')) ||
    !digit.every((n) => n.length === pertama.length)
      ? null
      : pertama.length
  const baru = { persis, angka, lebar, pemerintah, kode, kepala }
  indeksKk.set(daftar, baru)
  return baru
}

/**
 * The family a KK number names: the one of that number; else, for a number of digits whose
 * leading zeros a spreadsheet dropped ("811" for "0811", a General cell), the one family whose
 * number it is once its own zeros go. Two such families: neither is guessed (`ganda`).
 */
export function keluargaDenganKk(
  daftar: readonly KeluargaRujukan[],
  nomorKk: string,
): { jenis: 'satu'; keluarga: KeluargaRujukan } | { jenis: 'tidak' } | { jenis: 'ganda' } {
  const i = indeks(daftar)
  const persis = i.persis.get(nomorKk)
  if (persis !== undefined) return { jenis: 'satu', keluarga: persis }
  const angka = angkaNomorKk(nomorKk)
  if (angka === null) return { jenis: 'tidak' }
  const cocok = i.angka.get(angka) ?? []
  const [satu] = cocok
  if (satu === undefined) return { jenis: 'tidak' }
  return cocok.length === 1 ? { jenis: 'satu', keluarga: satu } : { jenis: 'ganda' }
}

/**
 * The parish's KK number width: the one length its all-digit numbers share when one of them
 * starts with 0 (a zero-padded series, "0811"); null when they differ or none is padded.
 */
export function lebarNomorKk(daftar: readonly KeluargaRujukan[]): number | null {
  return indeks(daftar).lebar
}

/** The family a "Nomor KK" cell names (release 18), or why none is named. */
export type CariKeluarga =
  | { jenis: 'satu'; keluarga: KeluargaRujukan; lewat: 'pemerintah' | 'kode' | 'paroki' }
  | { jenis: 'tidak' }
  /** `nomor`: a government number two families share; `kode`: a code that names two; `nol`: a parish number that fits two once its zeros go. */
  | { jenis: 'ganda'; sebab: 'nomor' | 'kode' | 'nol' }

/**
 * The family a person row joins (release 18): by the government number when the cell is one; else
 * by the Kode keluarga (any case), or by the parish number an earlier import gave
 * (`keluargaDenganKk`, a stripped zero put back). A value that names two families names neither:
 * the desk writes the one that tells them apart.
 */
export function keluargaDenganNomor(
  daftar: readonly KeluargaRujukan[],
  nilai: string | NomorKkBerkas,
): CariKeluarga {
  const baca = typeof nilai === 'string' ? bacaKolomNomorKk(nilai) : nilai
  const i = indeks(daftar)
  if (baca.jenis === 'pemerintah') {
    const cocok = i.pemerintah.get(baca.nomor) ?? []
    const [satu] = cocok
    if (satu === undefined) return { jenis: 'tidak' }
    return cocok.length === 1
      ? { jenis: 'satu', keluarga: satu, lewat: 'pemerintah' }
      : { jenis: 'ganda', sebab: 'nomor' }
  }
  if (baca.jenis !== 'kode') return { jenis: 'tidak' }
  const kode = i.kode.get(kunciKodeKeluarga(baca.kode) ?? '') ?? []
  const paroki = keluargaDenganKk(daftar, baca.kode)
  const calon = new Set(kode)
  if (paroki.jenis === 'satu') calon.add(paroki.keluarga)
  const [satu, ...lain] = calon
  if (satu === undefined)
    return paroki.jenis === 'ganda' ? { jenis: 'ganda', sebab: 'nol' } : { jenis: 'tidak' }
  if (lain.length > 0) return { jenis: 'ganda', sebab: 'kode' }
  return { jenis: 'satu', keluarga: satu, lewat: kode.includes(satu) ? 'kode' : 'paroki' }
}

/** The heads of the list's families holding a government number (release 18). */
function kepalaPemegangKk(daftar: readonly KeluargaRujukan[], nomor: string): (string | null)[] {
  return (indeks(daftar).pemerintah.get(nomor) ?? []).map((k) => k.kepala)
}

/** The heads of the list's families holding a Kode keluarga, compared in any case. */
function kepalaPemegangKode(daftar: readonly KeluargaRujukan[], kode: string): (string | null)[] {
  return (indeks(daftar).kode.get(kunciKodeKeluarga(kode) ?? '') ?? []).map((k) => k.kepala)
}

/**
 * Review I-3: the list's families whose parish number a file's code is, as a person row joins by
 * it (`keluargaDenganKk`): a number of digits whatever its leading zeros, any other value as
 * written. SATU's own download writes a parish-only family's number in "Nomor KK", and a
 * release-17 file has its "Nomor KK Katolik": both read as a code since release 18.
 */
function keluargaBernomorParoki(
  daftar: readonly KeluargaRujukan[],
  nomor: string,
): KeluargaRujukan[] {
  const i = indeks(daftar)
  const angka = angkaNomorKk(nomor)
  if (angka !== null) return i.angka.get(angka) ?? []
  const persis = i.persis.get(nomor)
  return persis === undefined ? [] : [persis]
}

/** A row already held for Tetap simpan. */
const ditahanKk = (masalah: readonly RowIssue[]): boolean =>
  masalah.some((m) => m.kind === 'duplikat')

/**
 * Whether two families of one head's name in one lingkungan may be one family, by their
 * government numbers (I-3 refinement): two different numbers are two families by Disdukcapil, and
 * in Papua many heads share a name. Without a number on either side they may be one: a family
 * that had only its parish number, its government number added now.
 */
function mungkinSatuKeluarga(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = a?.trim() ?? ''
  const y = b?.trim() ?? ''
  return x === '' || y === '' || x === y
}

/**
 * A KK number the file gives otherwise than the parish stores it: the row carries a note. Zeros
 * put back ("811" read 0811), or, when the file had more zeros than the stored number, read as
 * that family's number (final review 3, Minor 2: no zero was put back).
 */
function nolDikembalikan(ctx: Konteks, berkas: string, nomorKk: string): void {
  const kalimat =
    berkas.length < nomorKk.length ? pesan.nomorKkNolDepan : pesan.nomorKkDibacaSebagai
  catatan(ctx, 'nomor_kk', format(kalimat, { berkas, nomorKk }))
}

/**
 * A "Nomor KK" cell that cannot be used: Excel's exponent form, or a run of digits that is not 16.
 * The sentence never repeats the cell.
 */
function nomorKkRusak(ctx: Konteks, baca: NomorKkBerkas): boolean {
  if (baca.jenis === 'excel') galat(ctx, 'nomor_kk', pesan.nomorKkExcel)
  else if (baca.jenis === 'salah') galat(ctx, 'nomor_kk', ui.validasi.nomorKk16)
  else return false
  return true
}

/** A hold for "Tetap simpan" on the row (release 18): another family holds its number or code. */
function tahan(ctx: Konteks, field: string, message: string | null): void {
  if (message !== null) ctx.masalah.push({ row: ctx.baris, field, message, kind: 'duplikat' })
}

/** The cell as the preview shows it: the value read, whichever it is. */
function nomorKkTertulis(baca: NomorKkBerkas, raw: string | undefined): string | null {
  if (baca.jenis === 'pemerintah') return baca.nomor
  if (baca.jenis === 'kode') return baca.kode
  return baca.jenis === 'kosong' ? null : bacaNomorKk(raw)
}

/** A keluarga template row. */
export function susunBarisKeluarga(masukan: MasukanBaris): BarisImpor {
  const { nilai, rujukan } = masukan
  const ctx: Konteks = {
    baris: masukan.baris,
    jenis: 'keluarga',
    masalah: [],
    sebutan: masukan.sebutan ?? SEBUTAN_BAWAAN,
  }

  // Release 18: "Nomor KK" is the government number when 16 digits, else the family's code; a
  // Kode keluarga column beside it wins over a second value. No parish number is read: SATU gives
  // it as the file is saved.
  const baca = bacaKolomNomorKk(nilai.nomor_kk)
  const kodeKolom = rapikanKodeKeluarga(nilai.kode_keluarga)
  let kode = kodeKolom
  if (baca.jenis === 'kode') {
    if (kodeKolom === null) kode = baca.kode
    else if (kunciKodeKeluarga(baca.kode) !== kunciKodeKeluarga(kodeKolom)) {
      catatan(ctx, 'nomor_kk', pesan.nomorKkBukanAngka)
    }
  } else if (baca.jenis === 'kosong' && masukan.tanpaKk !== true) {
    catatan(ctx, 'nomor_kk', pesan.nomorKkKosong)
  }
  nomorKkRusak(ctx, baca)
  const kkPemerintah = baca.jenis === 'pemerintah' ? baca.nomor : null
  // Another family holds the number or the code (a married child on the parents' KK): held for
  // Tetap simpan, never refused (owner, 2026-10-08).
  if (kkPemerintah !== null) {
    tahan(
      ctx,
      'nomor_kk',
      peringatanNomorKk(kepalaPemegangKk(rujukan.keluarga, kkPemerintah), null),
    )
  }
  if (kode !== null) {
    tahan(ctx, 'kode_keluarga', peringatanKode(kepalaPemegangKode(rujukan.keluarga, kode), null))
    // Review I-3: the code is a family's parish number (SATU's own download, a release-17 file
    // imported again): held, the family named by its head. One already named by its code is not
    // named twice.
    const berkode = new Set(indeks(rujukan.keluarga).kode.get(kunciKodeKeluarga(kode) ?? ''))
    const paroki = keluargaBernomorParoki(rujukan.keluarga, kode).filter((k) => !berkode.has(k))
    const nama = gabungNamaKepala(paroki.map((k) => k.kepala))
    if (nama !== null) tahan(ctx, 'nomor_kk_katolik', format(pesan.nomorParokiSama, { kode, nama }))
  }

  const lingkunganNama = rapikanTeks(nilai.lingkungan)
  let lingkunganId: string | null = null
  if (lingkunganNama === null) {
    galat(ctx, 'lingkungan', format(pesan.wajib, { kolom: labelKolom(ctx, 'lingkungan') }))
  } else {
    const unit = cariUnit(lingkunganNama, rujukan.lingkungan)
    if (unit.jenis === 'nilai') lingkunganId = unit.nilai.id
    else {
      galat(
        ctx,
        'lingkungan',
        format(isiUnit(pesan.lingkunganTidakDikenal, ctx.sebutan), { nama: lingkunganNama }),
      )
    }
  }

  const kepala = pisahNama(nilai.kepala_keluarga)
  // Review I-2, I-3: a live family of that lingkungan on the device whose head has the same name
  // may be this one (a file imported twice): held for Tetap simpan, whatever number or code the
  // row has, unless they held it already. The template has no birth date of the head.
  if (!ditahanKk(ctx.masalah) && lingkunganId !== null && kepala !== null) {
    const sama = (
      indeks(rujukan.keluarga).kepala.get(kunciKepala(lingkunganId, kepala.namaLengkap)) ?? []
    ).filter((k) => mungkinSatuKeluarga(k.nomorKkPemerintah, kkPemerintah))
    if (sama.length > 0) {
      const kalimat = isiUnit(pesan.kepalaSudahAda, ctx.sebutan)
      tahan(ctx, 'kepala_keluarga', format(kalimat, { nama: kepala.namaLengkap }))
    }
  }
  // The optional columns below never skip the family (uji D2): a value that does not read is
  // dropped with a note.
  const telepon = ambil(ctx, 'telepon', nilai.telepon, bacaTelepon(nilai.telepon), 'kosongkan')
  const mulai = ambil(
    ctx,
    'tanggal_mulai',
    nilai.tanggal_mulai,
    bacaTanggal(nilai.tanggal_mulai),
    'kosongkan',
  )
  // Five digits, a leading apostrophe or an Excel ".0" dropped; the note never repeats the cell.
  const kodePos = bacaDigit(nilai.kode_pos, 5)
  if (kodePos.jenis === 'gagal') catatan(ctx, 'kode_pos', pesan.kodePos)
  // The import's own date rules (two-digit years read), as for Tanggal terdaftar.
  const tanggalKk = ambil(
    ctx,
    'tanggal_kk',
    nilai.tanggal_kk,
    bacaTanggal(nilai.tanggal_kk),
    'kosongkan',
  )
  // Release 19: the housing, as the parish data sheet words it.
  const statusRumah = ambil(
    ctx,
    'status_rumah',
    nilai.status_rumah,
    bacaStatusRumah(nilai.status_rumah),
    'catatan',
  )

  const fields: Record<string, unknown> = {
    lingkungan_id: lingkunganId,
    // Given as the file is saved (`beriNomorParoki`).
    nomor_kk_katolik: null,
    nomor_kk_pemerintah: kkPemerintah,
    kode_keluarga: kode,
    kepala_keluarga_id: null,
    alamat: rapikanTeks(nilai.alamat),
    rt_rw: rapikanTeks(nilai.rt_rw),
    kampung_desa: rapikanTeks(nilai.kampung_desa),
    kecamatan: rapikanTeks(nilai.kecamatan),
    kabupaten_kota: rapikanTeks(nilai.kabupaten_kota),
    provinsi: rapikanTeks(nilai.provinsi),
    kode_pos: kodePos.jenis === 'nilai' ? kodePos.nilai : null,
    tanggal_kk: tanggalKk,
    telepon,
    status_rumah: statusRumah,
    status: 'aktif',
    pendaftaran: null,
    tanggal_mulai: mulai,
    catatan: rapikanTeks(nilai.catatan),
  }

  if (lingkunganId !== null) {
    const parsed = keluargaImporSchema.safeParse(fields, { error: petaGalatZod })
    if (parsed.success) Object.assign(fields, parsed.data)
    else zodIssues(ctx, parsed.error.issues)
  }

  return {
    baris: masukan.baris,
    jenis: 'keluarga',
    fields,
    kepalaNama: kepala?.namaLengkap ?? null,
    keluarga: null,
    nomorKkBerkas: nomorKkTertulis(baca, nilai.nomor_kk),
    lingkunganNama:
      lingkunganId === null
        ? lingkunganNama
        : (rujukan.lingkungan.find((u) => u.id === lingkunganId)?.nama ?? lingkunganNama),
    masalah: ctx.masalah,
    perbarui: null,
  }
}

/** An umat template row. */
export function susunBarisUmat(masukan: MasukanBaris): BarisImpor {
  const { nilai, rujukan } = masukan
  const ctx: Konteks = {
    baris: masukan.baris,
    jenis: 'umat',
    masalah: [],
    sebutan: masukan.sebutan ?? SEBUTAN_BAWAAN,
  }

  const nama = pisahNama(nilai.nama_lengkap)
  if (nama === null) {
    galat(ctx, 'nama_lengkap', format(pesan.wajib, { kolom: labelKolom(ctx, 'nama_lengkap') }))
  }

  // Release 18: the family by whichever value the file has (`keluargaDenganNomor`). A sentence
  // repeats a parish number or a code, never a government number.
  const baca = bacaKolomNomorKk(nilai.nomor_kk)
  let keluarga: KeluargaRujukan | null = null
  if (baca.jenis === 'kosong') {
    galat(ctx, 'nomor_kk', format(pesan.wajib, { kolom: labelKolom(ctx, 'nomor_kk') }))
  } else if (!nomorKkRusak(ctx, baca)) {
    const cari = keluargaDenganNomor(rujukan.keluarga, baca)
    if (cari.jenis === 'satu') {
      keluarga = cari.keluarga
      if (cari.lewat === 'paroki' && baca.jenis === 'kode' && keluarga.nomorKk !== baca.kode) {
        nolDikembalikan(ctx, baca.kode, keluarga.nomorKk)
      }
    } else if (cari.jenis === 'tidak') galat(ctx, 'nomor_kk', pesan.keluargaTidakAda)
    else if (cari.sebab === 'nomor') galat(ctx, 'nomor_kk', pesan.keluargaGanda)
    else if (baca.jenis === 'kode') {
      const kalimat = cari.sebab === 'kode' ? pesan.kodeGanda : pesan.nomorKkNolGanda
      galat(ctx, 'nomor_kk', format(kalimat, { kode: baca.kode, nomorKk: baca.kode }))
    }
  }

  // Birth: a separate place and date, or one "Agats, 12-05-1987" cell.
  let tempatLahir = rapikanTeks(nilai.tempat_lahir)
  let tanggalLahirRaw = nilai.tanggal_lahir
  const gabungan = rapikanTeks(nilai.tempat_tanggal_lahir)
  if (gabungan !== null) {
    const koma = gabungan.lastIndexOf(',')
    if (koma === -1) {
      // Only a date, or only a place: a date reads, a place does not.
      if (bacaTanggal(gabungan).jenis === 'nilai') tanggalLahirRaw ??= gabungan
      else tempatLahir ??= gabungan
    } else {
      tempatLahir ??= rapikanTeks(gabungan.slice(0, koma))
      tanggalLahirRaw ??= gabungan.slice(koma + 1)
    }
  }
  const tanggalLahir = ambil(
    ctx,
    'tanggal_lahir',
    tanggalLahirRaw,
    bacaTanggal(tanggalLahirRaw),
    'galat',
  )
  const tanggalMeninggal = ambil(
    ctx,
    'tanggal_meninggal',
    nilai.tanggal_meninggal,
    bacaTanggal(nilai.tanggal_meninggal),
    'galat',
  )
  if (tanggalLahir !== null && tanggalMeninggal !== null && tanggalMeninggal < tanggalLahir) {
    galat(ctx, 'tanggal_meninggal', pesan.meninggalSebelumLahir)
  }

  // Optional values that do not read are dropped with a note (uji D2); the NIK's never
  // repeats the cell.
  const nik = bacaDigit(nilai.nik, 16)
  if (nik.jenis === 'gagal') catatan(ctx, 'nik', pesan.nik)
  const telepon = ambil(ctx, 'telepon', nilai.telepon, bacaTelepon(nilai.telepon), 'kosongkan')
  const jenisKelamin = ambil(
    ctx,
    'jenis_kelamin',
    nilai.jenis_kelamin,
    bacaJenisKelamin(nilai.jenis_kelamin),
    'catatan',
  )
  const hubungan = ambil(ctx, 'hubungan', nilai.hubungan, bacaHubungan(nilai.hubungan), 'catatan')
  const statusGereja = ambil(
    ctx,
    'status_gereja',
    nilai.status_gereja,
    bacaStatusGereja(nilai.status_gereja),
    'catatan',
  )
  const statusPerkawinan = ambil(
    ctx,
    'status_perkawinan',
    nilai.status_perkawinan,
    bacaStatusPerkawinan(nilai.status_perkawinan),
    'catatan',
  )
  const pendidikan = ambil(
    ctx,
    'pendidikan',
    nilai.pendidikan,
    bacaPendidikan(nilai.pendidikan),
    'catatan',
  )
  const pekerjaan = ambil(
    ctx,
    'pekerjaan',
    nilai.pekerjaan,
    bacaPekerjaan(nilai.pekerjaan),
    'catatan',
  )
  const statusHidup = ambil(
    ctx,
    'status_hidup',
    nilai.status_hidup,
    bacaStatusHidup(nilai.status_hidup),
    'catatan',
  )
  const namaBaptis = rapikanNama(nilai.nama_baptis) ?? nama?.namaBaptis ?? null
  const emailTeks = rapikanTeks(nilai.email)?.toLowerCase() ?? null
  const email =
    emailTeks === null || emailSchema.safeParse(emailTeks).success
      ? emailTeks
      : dikosongkan(ctx, 'email', emailTeks)

  // Release 19: the parish data sheet's fields.
  const agamaBaca = bacaAgama(nilai.agama)
  let agama: AgamaUmat | null = null
  let statusDariAgama: StatusGereja | null = null
  if (agamaBaca.jenis === 'nilai') agama = agamaBaca.nilai
  else if (agamaBaca.jenis === 'gagal') {
    // An older file's "Agama" holding a church status ("Katekumen", "Bukan Katolik").
    const status = bacaStatusGereja(nilai.agama)
    if (status.jenis === 'nilai') statusDariAgama = status.nilai
    else kodeTidakDikenal(ctx, 'agama', rapikanTeks(nilai.agama) ?? '')
  }
  // Without a Status gereja column the religion decides it; with one, that column does.
  const selaras = selaraskanAgama({
    agama,
    statusGereja: statusGereja ?? statusDariAgama ?? 'katolik',
    agamaDiubah: statusGereja === null && statusDariAgama === null,
  })
  const agamaSebelumnya = ambil(
    ctx,
    'agama_sebelumnya',
    nilai.agama_sebelumnya,
    bacaAgama(nilai.agama_sebelumnya),
    'catatan',
  )
  // A church named for the religion ("Adven", "GKI Petra") is kept as Gereja asal.
  const gerejaAsal =
    rapikanTeks(nilai.gereja_asal) ??
    denominasiDari(nilai.agama) ??
    denominasiDari(nilai.agama_sebelumnya)
  // "Anak Ke"; with that cell empty, the number of an "Anak 2" hubungan.
  const anakKeSel = bacaAnakKe(nilai.anak_ke)
  const anakKe =
    anakKeSel.jenis === 'kosong'
      ? anakKeDari(nilai.hubungan)
      : ambil(ctx, 'anak_ke', nilai.anak_ke, anakKeSel, 'kosongkan')
  const pendidikanSaatIni = ambil(
    ctx,
    'pendidikan_saat_ini',
    nilai.pendidikan_saat_ini,
    bacaPendidikanSaatIni(nilai.pendidikan_saat_ini),
    'catatan',
  )
  // A level read from the start of a longer cell says what it was read as (review minor 3).
  for (const [id, jenis] of [
    ['pendidikan', 'dukcapil'],
    ['pendidikan_saat_ini', 'saat_ini'],
  ] as const) {
    const hasil = levelDariAwalan(nilai[id], jenis)
    if (hasil !== null) {
      const sel = rapikanTeks(nilai[id]) ?? ''
      catatan(
        ctx,
        id,
        format(pesan.dibacaSebagai, { kolom: labelKolom(ctx, id), nilai: sel, hasil }),
      )
    }
  }
  // A school written with its level ("SMA Negeri 1 Agats") gives the school when none is.
  const namaSekolah =
    rapikanTeks(nilai.nama_sekolah) ??
    sekolahDariPendidikan(nilai.pendidikan_saat_ini) ??
    sekolahDariPendidikan(nilai.pendidikan)
  const tahunLulus = ambil(
    ctx,
    'tahun_lulus',
    nilai.tahun_lulus,
    bacaTahun(nilai.tahun_lulus),
    'kosongkan',
  )
  const keterampilanTeks = rapikanTeks(nilai.keterampilan)
  const keterampilan = keterampilanTeks === null ? [] : bacaKeterampilan(keterampilanTeks)
  const anggotaCu = ambil(
    ctx,
    'anggota_cu',
    nilai.anggota_cu,
    bacaAnggotaCu(nilai.anggota_cu),
    'catatan',
  )
  const statusRumah = ambil(
    ctx,
    'status_rumah',
    nilai.status_rumah,
    bacaStatusRumah(nilai.status_rumah),
    'catatan',
  )
  const referensi = bacaReferensi(ctx, nilai)
  const profil = masukan.denganProfil === true ? bacaProfil(ctx, nilai) : null

  const fields: Record<string, unknown> = {
    lingkungan_id: keluarga?.lingkunganId ?? null,
    nama_lengkap: nama?.namaLengkap ?? '',
    nama_baptis: namaBaptis,
    nama_panggilan: rapikanNama(nilai.nama_panggilan),
    jenis_kelamin: jenisKelamin,
    tempat_lahir: tempatLahir,
    tanggal_lahir: tanggalLahir,
    nik: nik.jenis === 'nilai' ? nik.nilai : null,
    nomor_akta_lahir: bacaNomorTeks(nilai.nomor_akta_lahir),
    status_gereja: selaras.statusGereja,
    agama: selaras.agama,
    agama_sebelumnya: agamaSebelumnya,
    gereja_asal: gerejaAsal,
    anak_ke: anakKe,
    status_perkawinan: statusPerkawinan,
    pendidikan_kode: pendidikan,
    pendidikan_saat_ini: pendidikanSaatIni,
    jurusan: rapikanTeks(nilai.jurusan),
    nama_sekolah: namaSekolah,
    tahun_lulus: tahunLulus,
    pekerjaan_kode: pekerjaan,
    profesi: rapikanTeks(nilai.profesi),
    anggota_cu: anggotaCu,
    keterampilan: keterampilan.length === 0 ? null : keterampilan,
    telepon,
    email,
    keluarga_id: keluarga?.id ?? null,
    hubungan,
    ayah_id: null,
    ibu_id: null,
    nama_ayah: rapikanNama(nilai.nama_ayah),
    nama_ibu: rapikanNama(nilai.nama_ibu),
    status_hidup: tanggalMeninggal !== null ? 'meninggal' : (statusHidup ?? 'hidup'),
    tanggal_meninggal: tanggalMeninggal,
    baptis_id: null,
    komuni_id: null,
    krisma_id: null,
    referensi_sakramen_luar: referensi,
    catatan: rapikanTeks(nilai.catatan),
  }

  if (nama !== null) {
    const parsed = umatSchema.safeParse(fields, { error: petaGalatZod })
    if (parsed.success) Object.assign(fields, parsed.data)
    else
      zodIssues(ctx, parsed.error.issues, {
        pendidikan_kode: 'pendidikan',
        pekerjaan_kode: 'pekerjaan',
      })
  }

  return {
    baris: masukan.baris,
    jenis: 'umat',
    fields,
    kepalaNama: null,
    keluarga,
    nomorKkBerkas: nomorKkTertulis(baca, nilai.nomor_kk),
    lingkunganNama: null,
    masalah: ctx.masalah,
    perbarui: null,
    profil,
    statusRumah,
    statusBerkas: statusGereja ?? statusDariAgama,
    agamaBerkas: agama,
  }
}

/**
 * The sacraments a person row gives from elsewhere (release 19), as `referensi_sakramen_luar`:
 * each with its parish, the certificate's number, the church, the date and, for a baptism and a
 * marriage, its kind. A certificate naming only its church has that church as its parish too; one
 * naming neither is dropped with a note (the parish is what the reference is). Null when none.
 */
function bacaReferensi(
  ctx: Konteks,
  nilai: Readonly<Record<string, string>>,
): ReferensiSakramenLuar | null {
  const out: Record<string, Record<string, unknown>> = {}
  for (const k of SAKRAMEN) {
    const sel = (f: string) => nilai[`${k}_${f}`]
    const tanggal = ambil(
      ctx,
      `${k}_tanggal`,
      sel('tanggal'),
      bacaTanggal(sel('tanggal')),
      'kosongkan',
    )
    const gereja = rapikanTeks(sel('gereja'))
    const isian: Record<string, unknown> = {
      nomor_surat: bacaNomorTeks(sel('nomor_surat')),
      gereja,
      tanggal,
      jenis_baptis:
        k === 'baptis'
          ? ambil(
              ctx,
              'jenis_baptis',
              nilai.jenis_baptis,
              bacaJenisBaptis(nilai.jenis_baptis),
              'catatan',
            )
          : null,
      jenis_perkawinan:
        k === 'perkawinan'
          ? ambil(
              ctx,
              'jenis_perkawinan',
              nilai.jenis_perkawinan,
              bacaJenisPerkawinan(nilai.jenis_perkawinan),
              'catatan',
            )
          : null,
    }
    const paroki = rapikanTeks(sel('paroki')) ?? gereja
    const ada = Object.entries(isian).filter(([, v]) => v !== null)
    if (paroki === null) {
      if (ada.length > 0) {
        catatan(
          ctx,
          `${k}_paroki`,
          format(pesan.sakramenTanpaParoki, { sakramen: ui.impor.sakramen[k] }),
        )
      }
      continue
    }
    out[k] = { paroki, ...Object.fromEntries(ada) }
  }
  return Object.keys(out).length === 0 ? null : (out as ReferensiSakramenLuar)
}

/** The sensitive fields of a person row (release 19), each emptied with a note when unread. */
function bacaProfil(
  ctx: Konteks,
  nilai: Readonly<Record<string, string>>,
): Record<string, unknown> {
  return {
    golongan_darah: ambil(
      ctx,
      'golongan_darah',
      nilai.golongan_darah,
      bacaGolonganDarah(nilai.golongan_darah),
      'catatan',
    ),
    suku: rapikanSuku(nilai.suku),
    pendapatan_bulanan: ambil(
      ctx,
      'pendapatan_bulanan',
      nilai.pendapatan_bulanan,
      bacaPendapatan(nilai.pendapatan_bulanan),
      'kosongkan',
    ),
    asuransi_kesehatan: ambil(
      ctx,
      'asuransi_kesehatan',
      nilai.asuransi_kesehatan,
      bacaAsuransi(nilai.asuransi_kesehatan),
      'catatan',
    ),
  }
}

export function susunBaris(jenis: JenisImpor, masukan: MasukanBaris): BarisImpor {
  const b = jenis === 'keluarga' ? susunBarisKeluarga(masukan) : susunBarisUmat(masukan)
  // A letter the file's encoding lost (a damaged UTF-8 byte, read as U+FFFD so the file's other
  // accents survive: final review 3, Minor 6): the row is refused, never saved with it.
  const kolom = kolomTarget(jenis, masukan.sebutan ?? SEBUTAN_BAWAAN)
  for (const [id, nilai] of Object.entries(masukan.nilai)) {
    if (!nilai.includes('\ufffd') || galatDi(b, id)) continue
    const label = kolom.find((k) => k.id === id)?.label ?? id
    b.masalah.push({
      row: b.baris,
      field: id,
      message: format(pesan.hurufRusak, { kolom: label }),
      kind: 'galat',
    })
  }
  return b
}

// ---------------------------------------------------------------------------
// Duplicates within the file and against the device
// ---------------------------------------------------------------------------

/** True when two partial birth dates can be the same day (equal, or one the other's prefix, or unknown). */
export function tanggalLahirCocok(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return true
  return a === b || a.startsWith(b) || b.startsWith(a)
}

/** The comparison key of a person: the name's alias key. */
export function kunciOrang(nama: string): string {
  return kunciTeks(nama)
}

/** True when the row already carries an error on `field`. */
const galatDi = (b: BarisImpor, field: string): boolean =>
  b.masalah.some((m) => m.field === field && m.kind === 'galat')

/** A row that will not be saved whatever the clerk ticks. */
const ditolak = (b: BarisImpor): boolean => b.masalah.some((m) => m.kind === 'galat')

/** A family row's value as text, or null. */
const nilaiTeks = (b: BarisImpor, kolom: string): string | null => {
  const v = b.fields[kolom]
  return typeof v === 'string' && v !== '' ? v : null
}

/** An earlier family row of the file by its head: its row and its government number. */
interface KepalaBerkas {
  baris: number
  nomor: string | null
}

/**
 * The head and lingkungan of a family row against the file's earlier rows (review I-2, I-3): a
 * row no number, code or family on the device held already is held when an earlier row has the
 * same head in the same lingkungan, unless their government numbers differ (two families by
 * Disdukcapil, `mungkinSatuKeluarga`). Every row's head counts as an earlier one, held or not.
 */
function tandaiKepalaBerkas(b: BarisImpor, kepalaKk: Map<string, KepalaBerkas[]>): void {
  const lingkunganId = nilaiTeks(b, 'lingkungan_id')
  if (lingkunganId === null || b.kepalaNama === null) return
  const kunci = kunciKepala(lingkunganId, b.kepalaNama)
  const nomor = nilaiTeks(b, 'nomor_kk_pemerintah')
  const sebelum = kepalaKk.get(kunci) ?? []
  kepalaKk.set(kunci, [...sebelum, { baris: b.baris, nomor }])
  const pertama = sebelum.find((k) => mungkinSatuKeluarga(k.nomor, nomor))?.baris
  if (pertama === undefined || ditahanKk(b.masalah)) return
  b.masalah.push({
    row: b.baris,
    field: 'kepala_keluarga',
    message: format(pesan.kepalaSamaBaris, { nama: b.kepalaNama, baris: pertama }),
    duplicateOf: `baris:${pertama}`,
    kind: 'duplikat',
  })
}

/**
 * A Kode keluarga as an earlier row of the same file is compared with it: a number of digits
 * as a parish number, whatever its leading zeros (review I-3: "0231" and "231" are one family's
 * number in a file of parish numbers); any other code in any case (`kunciKodeKeluarga`).
 */
function kunciKodeBerkas(kode: string | null): string | null {
  const angka = kode === null ? null : angkaNomorKk(kode)
  return angka === null ? kunciKodeKeluarga(kode) : `#${angka}`
}

/**
 * Mark rows that repeat an earlier row of the same file: the same government KK number or Kode
 * keluarga (keluarga, release 18: a hold, never Bermasalah, so the first row is the family and
 * Tetap simpan saves a second one on a shared number; a code of digits as a parish number), for
 * a family row held by neither the same head in the same lingkungan (review I-2, I-3: a hold),
 * the same NIK (umat, an error: one NIK, one person) or the same person by name and birth (umat,
 * a hold). A refused row is no earlier row. Mutates the rows.
 */
export function tandaiDuplikatBerkas(rows: readonly BarisImpor[]): void {
  const kk = new Map<string, number>()
  const kodeKk = new Map<string, number>()
  const kepalaKk = new Map<string, KepalaBerkas[]>()
  const nik = new Map<string, number>()
  const orang = new Map<string, { baris: number; lahir: string | null }[]>()
  for (const b of rows) {
    if (b.jenis === 'keluarga') {
      if (ditolak(b)) continue
      const nomor = nilaiTeks(b, 'nomor_kk_pemerintah')
      const kode = nilaiTeks(b, 'kode_keluarga')
      const kunciKode = kunciKodeBerkas(kode)
      const nomorPertama = nomor === null ? undefined : kk.get(nomor)
      const kodePertama = kunciKode === null ? undefined : kodeKk.get(kunciKode)
      if (nomorPertama !== undefined) {
        b.masalah.push({
          row: b.baris,
          field: 'nomor_kk',
          message: format(pesan.nomorKkSamaBaris, { baris: nomorPertama }),
          duplicateOf: `baris:${nomorPertama}`,
          kind: 'duplikat',
        })
      } else if (kodePertama !== undefined) {
        b.masalah.push({
          row: b.baris,
          field: 'kode_keluarga',
          message: format(pesan.kodeSamaBaris, { kode: kode ?? '', baris: kodePertama }),
          duplicateOf: `baris:${kodePertama}`,
          kind: 'duplikat',
        })
      }
      if (nomor !== null && nomorPertama === undefined) kk.set(nomor, b.baris)
      if (kunciKode !== null && kodePertama === undefined) kodeKk.set(kunciKode, b.baris)
      tandaiKepalaBerkas(b, kepalaKk)
      continue
    }
    const nomorNik = typeof b.fields.nik === 'string' ? b.fields.nik : ''
    if (nomorNik !== '') {
      const first = nik.get(nomorNik)
      if (first === undefined) nik.set(nomorNik, b.baris)
      else if (!galatDi(b, 'nik')) {
        b.masalah.push({
          row: b.baris,
          field: 'nik',
          message: format(pesan.nikSamaBaris, { baris: first }),
          duplicateOf: `baris:${first}`,
          kind: 'galat',
        })
      }
    }
    const nama = typeof b.fields.nama_lengkap === 'string' ? b.fields.nama_lengkap : ''
    if (nama === '') continue
    const key = kunciOrang(nama)
    const lahir = typeof b.fields.tanggal_lahir === 'string' ? b.fields.tanggal_lahir : null
    const earlier = orang.get(key) ?? []
    const same = earlier.find((e) => tanggalLahirCocok(e.lahir, lahir))
    if (same !== undefined) {
      b.masalah.push({
        row: b.baris,
        message: format(teksPratinjau.samaDenganBaris, { baris: same.baris }),
        duplicateOf: `baris:${same.baris}`,
        kind: 'duplikat',
      })
    }
    earlier.push({ baris: b.baris, lahir })
    orang.set(key, earlier)
  }
}

/** A person row's NIK as the device and the server are asked about it. */
export interface NikDitanya {
  baris: number
  nik: string
  /** The person a completing row rewrites: never its own holder. */
  umatId: string | null
}

/** The NIKs of the person rows not refused yet (a hold for "Tetap simpan" is still asked). */
export function nikDitanya(rows: readonly BarisImpor[]): NikDitanya[] {
  return rows.flatMap((b) =>
    b.jenis === 'umat' && !ditolak(b) && typeof b.fields.nik === 'string' && b.fields.nik !== ''
      ? [{ baris: b.baris, nik: b.fields.nik, umatId: b.perbarui?.umatId ?? null }]
      : [],
  )
}

/** A new family's government number and code, as the server is asked about them (release 18). */
export interface NomorDitanya {
  baris: number
  nomorKkPemerintah: string | null
  kodeKeluarga: string | null
}

/**
 * The government numbers and codes of the new families not refused yet (a hold is still asked:
 * the server may name another parish's family). A code longer than a question allows (a note in
 * the cell) is not asked: one such cell made the server refuse the whole chunk, and the rest of
 * the file went unasked (final review 3, Minor 3).
 */
export function nomorDitanya(rows: readonly BarisImpor[]): NomorDitanya[] {
  return rows.flatMap((b) => {
    if (b.jenis !== 'keluarga' || ditolak(b)) return []
    const nomorKkPemerintah = nilaiTeks(b, 'nomor_kk_pemerintah')
    const kode = nilaiTeks(b, 'kode_keluarga')
    const kodeKeluarga = kode !== null && kode.length <= GANDA_KODE_PANJANG ? kode : null
    return nomorKkPemerintah === null && kodeKeluarga === null
      ? []
      : [{ baris: b.baris, nomorKkPemerintah, kodeKeluarga }]
  })
}

/**
 * Another live person holds the row's NIK (owner, 2026-10-04: one NIK, one person): the row is
 * refused in the preview, never saved, so no op the server refuses (a sync problem that keeps
 * retrying) is queued. `pemegang`: that person's name on this device; null when only the server
 * knows them, who are never named. No "Tetap simpan" lifts it.
 */
export function tandaiNikDipakai(b: BarisImpor, pemegang: string | null): void {
  if (galatDi(b, 'nik')) return
  b.masalah.push({
    row: b.baris,
    field: 'nik',
    message:
      pemegang === null ? pesan.nikTercatatLain : format(pesan.nikTercatat, { nama: pemegang }),
    kind: 'galat',
  })
}

/**
 * The hold a row carries for another family's number or code: one sentence, the device's and the
 * server's holders together, each name once (an earlier row of the file is a hold of its own).
 */
function tahanDariServer(b: BarisImpor, field: string, kalimat: string | null): void {
  if (kalimat === null) return
  const ada = b.masalah.find(
    (m) => m.kind === 'duplikat' && m.field === field && m.duplicateOf === undefined,
  )
  if (ada !== undefined) ada.message = kalimat
  else b.masalah.push({ row: b.baris, field, message: kalimat, kind: 'duplikat' })
}

/**
 * The server's answer for a new family's government number (release 18, POST /api/sync/ganda):
 * the heads of this parish's families holding it, and whether another parish's does, never which.
 * The row is held for Tetap simpan, never refused (owner, 2026-10-08).
 */
export function tandaiNomorKkDipakai(b: BarisImpor, pemakai: PemakaiKk, rujukan: Rujukan): void {
  const nomor = nilaiTeks(b, 'nomor_kk_pemerintah')
  if (nomor === null) return
  if (pemakai.paroki_ini.length === 0 && pemakai.luar_lingkup !== true && !pemakai.paroki_lain) {
    return
  }
  tahanDariServer(
    b,
    'nomor_kk',
    peringatanNomorKk(kepalaPemegangKk(rujukan.keluarga, nomor), pemakai),
  )
}

/** The same for a new family's Kode keluarga: the heads of this parish's families holding it. */
export function tandaiKodeDipakai(b: BarisImpor, pemakai: PemakaiKode, rujukan: Rujukan): void {
  const kode = nilaiTeks(b, 'kode_keluarga')
  if (kode === null || (pemakai.paroki_ini.length === 0 && pemakai.luar_lingkup !== true)) return
  tahanDariServer(
    b,
    'kode_keluarga',
    peringatanKode(kepalaPemegangKode(rujukan.keluarga, kode), pemakai),
  )
}

/**
 * Apply the worker's duplicate answer to an umat row: the same person in the same family
 * (the head created by a keluarga import, or a re-import) is completed rather than
 * duplicated; anyone else of that name and birth holds the row for "Tetap simpan".
 */
export function terapkanDuplikat(b: BarisImpor, hit: DuplikatUmat | null): void {
  if (hit === null || b.jenis !== 'umat') return
  if (b.masalah.some((m) => m.kind === 'galat')) return
  if (b.keluarga !== null && hit.keluargaId === b.keluarga.id) {
    b.perbarui = hit
    b.masalah.push({
      row: b.baris,
      message: format(teksPratinjau.melengkapi, {
        nama: hit.nama,
        nomorKk: nomorKkDalamKurung(hit.nomorKk ?? nomorKkRujukan(b.keluarga)),
      }),
      duplicateOf: hit.umatId,
      kind: 'catatan',
    })
    lepasSakramenTertaut(b, hit.tertaut ?? [])
    return
  }
  const tahun = hit.tanggalLahir === null ? '—' : hit.tanggalLahir.slice(0, 4)
  b.masalah.push({
    row: b.baris,
    message: format(ui.impor.pratinjau.mungkinSudahAda, {
      nama: hit.nama,
      tahun,
      nomorKk: nomorKkDalamKurung(hit.nomorKk ?? '—'),
    }),
    duplicateOf: hit.umatId,
    kind: 'duplikat',
  })
}

/**
 * A sacrament the person being completed has linked to an entry of this parish's book (release
 * 19): the book is the record, so the file's reference to it is dropped, with a note.
 */
function lepasSakramenTertaut(b: BarisImpor, tertaut: readonly KunciSakramenImpor[]): void {
  const referensi = b.fields.referensi_sakramen_luar
  if (typeof referensi !== 'object' || referensi === null || tertaut.length === 0) return
  const lepas = SAKRAMEN.filter((k) => tertaut.includes(k) && k in referensi)
  if (lepas.length === 0) return
  for (const k of lepas) {
    b.masalah.push({
      row: b.baris,
      field: `${k}_paroki`,
      message: format(pesan.sakramenTertaut, { sakramen: ui.impor.sakramen[k] }),
      kind: 'catatan',
    })
  }
  const sisa = Object.entries(referensi).filter(([k]) => !lepas.some((l) => l === k))
  b.fields.referensi_sakramen_luar = sisa.length === 0 ? null : Object.fromEntries(sisa)
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface TulisanBaris {
  baris: number
  writes: LocalWrite[]
}

function kolomBersih<T extends Record<string, unknown>>(
  fields: T,
  keep: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const k of keep) if (k in fields && fields[k] !== undefined) out[k] = fields[k]
  return out
}

const KOLOM_KELUARGA_TULIS: readonly (keyof Keluarga)[] = [
  'lingkungan_id',
  'nomor_kk_katolik',
  'nomor_kk_pemerintah',
  'kode_keluarga',
  'kepala_keluarga_id',
  'alamat',
  'rt_rw',
  'kampung_desa',
  'kecamatan',
  'kabupaten_kota',
  'provinsi',
  'kode_pos',
  'tanggal_kk',
  'telepon',
  'status_rumah',
  'status',
  'pendaftaran',
  'tanggal_mulai',
  'catatan',
]

const KOLOM_UMAT_TULIS: readonly (keyof Umat)[] = [
  'lingkungan_id',
  'nama_lengkap',
  'nama_baptis',
  'nama_panggilan',
  'jenis_kelamin',
  'tempat_lahir',
  'tanggal_lahir',
  'nik',
  'nomor_akta_lahir',
  'status_gereja',
  'agama',
  'agama_sebelumnya',
  'gereja_asal',
  'anak_ke',
  'status_perkawinan',
  'pendidikan_kode',
  'pendidikan_saat_ini',
  'jurusan',
  'nama_sekolah',
  'tahun_lulus',
  'pekerjaan_kode',
  'profesi',
  'anggota_cu',
  'keterampilan',
  'telepon',
  'email',
  'keluarga_id',
  'hubungan',
  'nama_ayah',
  'nama_ibu',
  'status_hidup',
  'tanggal_meninggal',
  'referensi_sakramen_luar',
  'catatan',
]

/**
 * A completing row's sacraments merged onto the person's stored ones (release 19): each the file
 * gives completes its own, the others stay. Null when the file gives none, or when the stored
 * ones were not read (never written over unseen).
 */
function gabungReferensi(hit: DuplikatUmat, dariBerkas: unknown): Record<string, unknown> | null {
  if (typeof dariBerkas !== 'object' || dariBerkas === null || hit.referensiLuar === undefined) {
    return null
  }
  const tersimpan: Record<string, unknown> = { ...(hit.referensiLuar ?? {}) }
  for (const [k, ref] of Object.entries(dariBerkas)) {
    const lama = tersimpan[k]
    tersimpan[k] = { ...(typeof lama === 'object' && lama !== null ? lama : {}), ...ref }
  }
  return tersimpan
}

/**
 * The church status and the religion a completing row writes (review I-2, ruling of 2026-10-09),
 * against the person as stored (the worker's duplicate answer):
 * - a Status gereja the file states (its column, or an older file's "Agama: Katekumen") is
 *   written, the religion kept in agreement with it (`selaraskanAgama`, the status decides);
 * - "Agama: Katolik" states no status (the parish's sheet says it of everyone): a stored
 *   katekumen or non-Catholic stays so, and the religion is written only to a Catholic without one;
 * - another religion is written, and makes a Catholic "Bukan Katolik" (a katekumen keeps the
 *   status and holds that religion until baptism).
 * Nothing is written when the file says neither.
 */
function agamaPerbarui(b: BarisImpor, hit: DuplikatUmat): Record<string, unknown> {
  const statusLama = (hit.statusGereja ?? null) as StatusGereja | null
  const agamaLama = (hit.agama ?? null) as AgamaUmat | null
  const agamaBerkas = (b.agamaBerkas ?? null) as AgamaUmat | null
  const statusBerkas = (b.statusBerkas ?? null) as StatusGereja | null
  if (statusBerkas !== null) {
    const s = selaraskanAgama({
      agama: agamaBerkas ?? agamaLama,
      statusGereja: statusBerkas,
      agamaDiubah: false,
    })
    const out: Record<string, unknown> = { status_gereja: s.statusGereja }
    if (s.agama !== agamaLama) out.agama = s.agama
    return out
  }
  if (agamaBerkas === null) return {}
  if (agamaBerkas === 'katolik') {
    return statusLama === 'katolik' && agamaLama === null ? { agama: 'katolik' } : {}
  }
  const s = selaraskanAgama({
    agama: agamaBerkas,
    statusGereja: statusLama ?? 'katolik',
    agamaDiubah: true,
  })
  return s.statusGereja === statusLama
    ? { agama: s.agama }
    : { agama: s.agama, status_gereja: s.statusGereja }
}

/** The person's sensitive fields the row read (release 19), under the person's own id. */
function tulisanProfilBaris(b: BarisImpor, umatId: string): LocalWrite[] {
  const fields = Object.fromEntries(
    Object.entries(b.profil ?? {}).filter(([, v]) => v !== null && v !== undefined),
  )
  return Object.keys(fields).length === 0 ? [] : [{ table: 'umat_profil', row_id: umatId, fields }]
}

/** What a person row writes to its family: a headless family's head, and the housing it gives. */
function tulisanKeluargaOrang(b: BarisImpor, umatId: string, hubungan: unknown): LocalWrite[] {
  const keluarga = b.keluarga
  if (keluarga === null) return []
  const fields: Record<string, unknown> = {}
  if (keluarga.kepalaId === null && hubungan === 'kepala') fields.kepala_keluarga_id = umatId
  if (typeof b.statusRumah === 'string') fields.status_rumah = b.statusRumah
  return Object.keys(fields).length === 0
    ? []
    : [{ table: 'keluarga', row_id: keluarga.id, fields }]
}

/**
 * The ops of one ready row. A keluarga row creates the family, its head (when named) and
 * the head's membership. An umat row creates the person and their membership, and names
 * them head of a family that has none; a row marked `perbarui` rewrites the existing
 * person with the values the file supplied, leaving the rest as they were. Release 19: the
 * family's housing goes to the family, the sensitive fields to `umat_profil`.
 */
export function tulisanBaris(b: BarisImpor, newId: () => string = uuidv7): TulisanBaris {
  const writes: LocalWrite[] = []
  if (b.jenis === 'keluarga') {
    // Release 18: the parish number is SATU's, given as the file is saved (`beriNomorParoki`).
    if (nilaiTeks(b, 'nomor_kk_katolik') === null) {
      throw new Error(`Impor: baris ${b.baris} belum diberi nomor KK paroki`)
    }
    const keluargaId = newId()
    const fields = kolomBersih(b.fields, KOLOM_KELUARGA_TULIS)
    const lingkunganId = typeof fields.lingkungan_id === 'string' ? fields.lingkungan_id : null
    if (b.kepalaNama !== null) {
      const umatId = newId()
      fields.kepala_keluarga_id = umatId
      writes.push({ table: 'keluarga', row_id: keluargaId, fields })
      writes.push({
        table: 'umat',
        row_id: umatId,
        fields: {
          lingkungan_id: lingkunganId,
          nama_lengkap: b.kepalaNama,
          status_gereja: 'katolik',
          status_hidup: 'hidup',
          keluarga_id: keluargaId,
          hubungan: 'kepala',
        },
      })
      writes.push({
        table: 'anggota_keluarga',
        row_id: newId(),
        fields: {
          lingkungan_id: lingkunganId,
          umat_id: umatId,
          keluarga_id: keluargaId,
          hubungan: 'kepala',
          mulai: typeof fields.tanggal_mulai === 'string' ? fields.tanggal_mulai : null,
          selesai: null,
          alasan_selesai: null,
        },
      })
    } else {
      writes.push({ table: 'keluarga', row_id: keluargaId, fields })
    }
    return { baris: b.baris, writes }
  }

  const fields = kolomBersih(b.fields, KOLOM_UMAT_TULIS)
  const keluarga = b.keluarga
  if (b.perbarui !== null) {
    const supplied: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(fields)) {
      if (v === null || v === undefined) continue
      // Defaults the file did not say anything about must not overwrite a stored value; the
      // church status and the religion are reconciled with the person as stored (review I-2).
      if (k === 'status_hidup' || k === 'referensi_sakramen_luar') continue
      if (k === 'status_gereja' || k === 'agama') continue
      supplied[k] = v
    }
    Object.assign(supplied, agamaPerbarui(b, b.perbarui))
    const referensi = gabungReferensi(b.perbarui, fields.referensi_sakramen_luar)
    if (referensi !== null) supplied.referensi_sakramen_luar = referensi
    if (keluarga !== null) {
      supplied.keluarga_id = keluarga.id
      supplied.lingkungan_id = keluarga.lingkunganId
    }
    if (typeof fields.status_hidup === 'string' && fields.tanggal_meninggal !== null)
      supplied.status_hidup = fields.status_hidup
    writes.push({ table: 'umat', row_id: b.perbarui.umatId, fields: supplied })
    writes.push(...tulisanKeluargaOrang(b, b.perbarui.umatId, fields.hubungan))
    writes.push(...tulisanProfilBaris(b, b.perbarui.umatId))
    return { baris: b.baris, writes }
  }

  const umatId = newId()
  writes.push({ table: 'umat', row_id: umatId, fields })
  if (keluarga !== null) {
    writes.push({
      table: 'anggota_keluarga',
      row_id: newId(),
      fields: {
        lingkungan_id: keluarga.lingkunganId,
        umat_id: umatId,
        keluarga_id: keluarga.id,
        hubungan: typeof fields.hubungan === 'string' ? fields.hubungan : 'famili_lain',
        mulai: null,
        selesai: null,
        alasan_selesai: null,
      },
    })
    writes.push(...tulisanKeluargaOrang(b, umatId, fields.hubungan))
  }
  writes.push(...tulisanProfilBaris(b, umatId))
  return { baris: b.baris, writes }
}

/**
 * The parish numbers of a file's new families (release 18, owner 2026-10-08: generated, never
 * typed or read from a file): `awal`, the parish's next number as the save reads it (the device's
 * or the server's, the higher; retired numbers below it), then the ones after it, in the file's
 * order, the width kept. Copies of the family rows; the rows the preview holds stay as they were.
 */
export function beriNomorParoki(rows: readonly BarisImpor[], awal: string): BarisImpor[] {
  let nomor = awal
  return rows.map((b) => {
    if (b.jenis !== 'keluarga') return b
    const baru = { ...b, fields: { ...b.fields, nomor_kk_katolik: nomor } }
    nomor = nomorKkSesudah(nomor)
    return baru
  })
}

/**
 * Group ready rows into batches of about `ukuran` writes, never splitting a row, so one
 * `apply` per batch keeps the worker's transactions short and the UI free between them.
 */
export function kelompokkanTulisan(
  tulisan: readonly TulisanBaris[],
  ukuran = 200,
): TulisanBaris[][] {
  const batches: TulisanBaris[][] = []
  let current: TulisanBaris[] = []
  let n = 0
  for (const t of tulisan) {
    if (n > 0 && n + t.writes.length > ukuran) {
      batches.push(current)
      current = []
      n = 0
    }
    current.push(t)
    n += t.writes.length
  }
  if (current.length > 0) batches.push(current)
  return batches
}

/** The columns a keluarga template row produced, for tests and the preview. */
export function kolomWajib(jenis: JenisImpor): KolomTarget[] {
  return kolomTarget(jenis).filter((k) => k.wajib)
}
